/**
 * DeliverableCollector - Service for collecting and organizing deliverables from conversation history
 */

export class DeliverableCollector {
  /**
   * Collect all deliverables from conversation history
   * @param {Array} conversationHistory - The conversation history from supervisor
   * @returns {Object} Organized deliverables by type and agent
   */
  static collect(conversationHistory) {
    const deliverables = {
      strategy: [],
      copy: [],
      seo: [],
      campaigns: [],
      social: [],
      design: [],
      data: [],
      email: [],
      video: [],
      final: null,
      raw: [] // All deliverables in order
    };

    if (!conversationHistory || !Array.isArray(conversationHistory)) {
      return deliverables;
    }

    conversationHistory.forEach((entry, index) => {
      if (entry.type === 'agent_output' && entry.content) {
        const deliverable = this.parseDeliverable(entry, index);

        // Add to raw list
        deliverables.raw.push(deliverable);

        // Categorize by agent type
        switch (entry.agent) {
          case 'strategist':
            deliverables.strategy.push(deliverable);
            break;
          case 'copywriter':
            deliverables.copy.push(deliverable);
            break;
          case 'seo_specialist':
            deliverables.seo.push(deliverable);
            break;
          case 'ppc_manager':
            deliverables.campaigns.push(deliverable);
            break;
          case 'social_media_manager':
            deliverables.social.push(deliverable);
            break;
          case 'designer':
            deliverables.design.push(deliverable);
            break;
          case 'data_analyst':
            deliverables.data.push(deliverable);
            break;
          case 'email_marketing':
            deliverables.email.push(deliverable);
            break;
          case 'video_producer':
            deliverables.video.push(deliverable);
            break;
          case 'client_manager':
            // Client manager creates the final summary
            deliverables.final = deliverable;
            break;
        }
      } else if (entry.type === 'completion' && entry.content) {
        // Project completion summary
        deliverables.final = this.parseDeliverable(entry, index);
      }
    });

    // Extract actionable items from deliverables
    deliverables.actions = this.extractActions(deliverables);

    return deliverables;
  }

  /**
   * Parse a single deliverable entry
   */
  static parseDeliverable(entry, index) {
    return {
      id: `del_${index}_${Date.now()}`,
      agent: entry.agent,
      agentName: entry.agentName || entry.agent,
      content: entry.content,
      timestamp: entry.timestamp || new Date().toISOString(),
      type: this.detectContentType(entry.content, entry.agent),
      metadata: this.extractMetadata(entry.content),
      actions: this.detectPossibleActions(entry.content, entry.agent)
    };
  }

  /**
   * Detect the type of content based on text analysis
   */
  static detectContentType(content, agent) {
    const types = {
      strategist: 'strategy',
      copywriter: 'copy',
      seo_specialist: 'seo',
      ppc_manager: 'campaign',
      social_media_manager: 'social',
      designer: 'design',
      data_analyst: 'analytics',
      email_marketing: 'email',
      video_producer: 'video',
      client_manager: 'summary'
    };

    return types[agent] || 'general';
  }

  /**
   * Extract metadata from content
   */
  static extractMetadata(content) {
    const metadata = {
      hasNumbers: /\d+/.test(content),
      hasBulletPoints: /^[\-\*•]/m.test(content),
      hasLinks: /https?:\/\//i.test(content),
      hasHashtags: /#\w+/g.test(content),
      wordCount: content.split(/\s+/).length,
      lineCount: content.split('\n').length
    };

    // Extract specific data patterns
    const budgetMatch = content.match(/תקציב[:\s]+([₪$]\s*[\d,]+)/i);
    if (budgetMatch) metadata.budget = budgetMatch[1];

    const percentMatch = content.match(/(\d+)%/g);
    if (percentMatch) metadata.percentages = percentMatch;

    return metadata;
  }

  /**
   * Detect possible actions from content
   */
  static detectPossibleActions(content, agent) {
    const actions = [];

    // Campaign creation actions
    if (content.includes('קמפיין') || content.includes('campaign')) {
      if (content.includes('Google') || content.includes('גוגל')) {
        actions.push({
          type: 'create_google_campaign',
          label: 'צור קמפיין ב-Google Ads',
          icon: 'faGoogle',
          confidence: 0.9
        });
      }

      if (content.includes('Facebook') || content.includes('פייסבוק') || content.includes('Meta')) {
        actions.push({
          type: 'create_fb_campaign',
          label: 'צור קמפיין ב-Facebook',
          icon: 'faFacebook',
          confidence: 0.9
        });
      }
    }

    // Copy actions
    if (agent === 'copywriter' && content.length > 100) {
      actions.push({
        type: 'copy_text',
        label: 'העתק טקסט',
        icon: 'faCopy',
        confidence: 1.0
      });

      actions.push({
        type: 'export_copy',
        label: 'ייצא כמסמך',
        icon: 'faFileWord',
        confidence: 0.8
      });
    }

    // SEO actions
    if (agent === 'seo_specialist') {
      actions.push({
        type: 'export_keywords',
        label: 'ייצא מילות מפתח',
        icon: 'faFileExcel',
        confidence: 0.9
      });
    }

    // Social media actions
    if (agent === 'social_media_manager') {
      actions.push({
        type: 'schedule_post',
        label: 'תזמן פוסט',
        icon: 'faCalendarAlt',
        confidence: 0.7
      });
    }

    // Universal export action
    actions.push({
      type: 'export_pdf',
      label: 'ייצא כ-PDF',
      icon: 'faFilePdf',
      confidence: 0.5
    });

    return actions;
  }

  /**
   * Extract all actionable items from all deliverables
   */
  static extractActions(deliverables) {
    const allActions = [];

    // Collect actions from all deliverables
    Object.values(deliverables).forEach(category => {
      if (Array.isArray(category)) {
        category.forEach(deliverable => {
          if (deliverable.actions && deliverable.actions.length > 0) {
            deliverable.actions.forEach(action => {
              allActions.push({
                ...action,
                source: deliverable.agentName,
                deliverableId: deliverable.id
              });
            });
          }
        });
      }
    });

    // Sort by confidence
    return allActions.sort((a, b) => (b.confidence || 0) - (a.confidence || 0));
  }

  /**
   * Get a summary of all deliverables
   */
  static getSummary(deliverables) {
    const summary = {
      totalDeliverables: deliverables.raw.length,
      byCategory: {},
      hasActions: deliverables.actions && deliverables.actions.length > 0,
      actionCount: deliverables.actions ? deliverables.actions.length : 0,
      hasFinalSummary: !!deliverables.final
    };

    // Count by category
    Object.keys(deliverables).forEach(key => {
      if (Array.isArray(deliverables[key])) {
        summary.byCategory[key] = deliverables[key].length;
      }
    });

    return summary;
  }
}